/**
 * UranIA PA — API client admin corpus & ingestion.
 *
 * Wrapper su:
 *   - ``POST /api/v1/admin/corpus/ingest`` (multipart, enqueue saga)
 *   - ``GET /api/v1/admin/corpus/ingest/{job_id}`` (Arq status polling)
 *
 * Boundary:
 *   - Bearer JWT da ``useAuthStore.accessToken``;
 *   - 401 step-up → surface ``acrRequired`` come per skills-api.
 */

import { useAuthStore } from '../auth/auth-store.ts';
import { loadSpaEnv } from '../env.ts';

export type CorpusTenantScope = 'core' | 'tenant';
export type CorpusDataClassification =
  | 'public'
  | 'internal'
  | 'restricted'
  | 'secret';

export interface CorpusIngestEnqueueDto {
  readonly job_id: string;
  readonly doc_id: string;
  readonly source_uri: string;
  readonly mime_type: string;
  readonly size_bytes: number;
}

export type CorpusJobStatus =
  | 'deferred'
  | 'queued'
  | 'in_progress'
  | 'complete'
  | 'not_found';

export interface CorpusJobStatusDto {
  readonly job_id: string;
  readonly status: CorpusJobStatus;
  readonly result: Record<string, unknown> | null;
  readonly error: string | null;
}

export class CorpusApiError extends Error {
  public readonly status: number;
  public readonly detail: string | null;
  constructor(status: number, title: string, detail: string | null) {
    super(title);
    this.name = 'CorpusApiError';
    this.status = status;
    this.detail = detail;
  }
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) return {};
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

async function parseProblem(response: Response): Promise<CorpusApiError> {
  let title = response.statusText || 'http_error';
  let detail: string | null = null;
  try {
    const body = (await response.json()) as { title?: unknown; detail?: unknown };
    if (typeof body.title === 'string' && body.title.length > 0) title = body.title;
    if (typeof body.detail === 'string') detail = body.detail;
  } catch {
    // no problem+json
  }
  return new CorpusApiError(response.status, title, detail);
}

export type CorpusCategoria =
  | 'eur_lex'
  | 'ministeri'
  | 'regioni'
  | 'corpus_scientifico';

export interface IngestCorpusInput {
  readonly file: File;
  readonly docId: string;
  readonly titolo: string;
  readonly categoria: CorpusCategoria;
  readonly tipoAtto?: string;
  readonly descrizione?: string;
  readonly fonte?: string;
  readonly anno?: number | null;
  readonly tags?: readonly string[];
  readonly tenantScope: CorpusTenantScope;
  readonly dataClassification: CorpusDataClassification;
  readonly regione?: string;
}

export async function enqueueCorpusIngest(
  input: IngestCorpusInput,
): Promise<CorpusIngestEnqueueDto> {
  const form = new FormData();
  form.append('file', input.file);
  form.append('doc_id', input.docId);
  form.append('titolo', input.titolo);
  form.append('categoria', input.categoria);
  if (input.tipoAtto !== undefined && input.tipoAtto.length > 0) {
    form.append('tipo_atto', input.tipoAtto);
  }
  if (input.descrizione !== undefined && input.descrizione.length > 0) {
    form.append('descrizione', input.descrizione);
  }
  if (input.fonte !== undefined && input.fonte.length > 0) {
    form.append('fonte', input.fonte);
  }
  if (input.anno !== undefined && input.anno !== null) {
    form.append('anno', String(input.anno));
  }
  if (input.tags !== undefined && input.tags.length > 0) {
    form.append('tags', input.tags.join(','));
  }
  form.append('tenant_scope', input.tenantScope);
  form.append('data_classification', input.dataClassification);
  if (typeof input.regione === 'string' && input.regione.length > 0) {
    form.append('regione', input.regione);
  }
  const response = await fetch(buildUrl('/api/v1/admin/corpus/ingest'), {
    method: 'POST',
    headers: { ...authHeader() },
    credentials: 'include',
    body: form,
  });
  if (!response.ok) {
    throw await parseProblem(response);
  }
  return (await response.json()) as CorpusIngestEnqueueDto;
}

export async function getCorpusJobStatus(jobId: string): Promise<CorpusJobStatusDto> {
  const response = await fetch(
    buildUrl(`/api/v1/admin/corpus/ingest/${encodeURIComponent(jobId)}`),
    {
      method: 'GET',
      headers: { Accept: 'application/json', ...authHeader() },
      credentials: 'include',
    },
  );
  if (!response.ok) {
    throw await parseProblem(response);
  }
  return (await response.json()) as CorpusJobStatusDto;
}

// ---------------------------------------------------------------------------
// Banca Dati: list / stats / delete
// ---------------------------------------------------------------------------

export type CorpusIngestionStatus =
  | 'queued'
  | 'in_progress'
  | 'complete'
  | 'failed'
  | 'unknown';

export interface CorpusDocumentDto {
  readonly doc_id: string;
  readonly tenant_id: string | null;
  readonly categoria: CorpusCategoria;
  readonly tipo_atto: string;
  readonly titolo: string;
  readonly descrizione: string;
  readonly fonte: string;
  readonly anno: number | null;
  readonly regione: string | null;
  readonly tags: readonly string[];
  readonly mime_type: string;
  readonly size_bytes: number;
  readonly ingestion_job_id: string | null;
  readonly ingestion_status: CorpusIngestionStatus;
  readonly created_at: string;
  readonly updated_at: string | null;
}

export interface ListCorpusDocumentsDto {
  readonly items: readonly CorpusDocumentDto[];
  readonly total: number;
}

export interface CorpusStatsDto {
  readonly total: number;
  readonly eur_lex: number;
  readonly ministeri: number;
  readonly regioni: number;
  readonly corpus_scientifico: number;
}

export interface ListCorpusDocumentsParams {
  readonly categoria?: CorpusCategoria | null;
  readonly q?: string;
  readonly anno?: number | null;
  readonly tipoAtto?: string | null;
  readonly tag?: string | null;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'GET',
    headers: { Accept: 'application/json', ...authHeader() },
    credentials: 'include',
  });
  if (!response.ok) {
    throw await parseProblem(response);
  }
  return (await response.json()) as T;
}

export async function listCorpusDocuments(
  params: ListCorpusDocumentsParams = {},
): Promise<ListCorpusDocumentsDto> {
  const search = new URLSearchParams();
  if (params.categoria !== undefined && params.categoria !== null) {
    search.set('categoria', params.categoria);
  }
  if (params.q !== undefined && params.q.length > 0) {
    search.set('q', params.q);
  }
  if (params.anno !== undefined && params.anno !== null) {
    search.set('anno', String(params.anno));
  }
  if (params.tipoAtto !== undefined && params.tipoAtto !== null && params.tipoAtto.length > 0) {
    search.set('tipo_atto', params.tipoAtto);
  }
  if (params.tag !== undefined && params.tag !== null && params.tag.length > 0) {
    search.set('tag', params.tag);
  }
  const qs = search.toString();
  const path = qs.length > 0 ? `/api/v1/admin/corpus/documents?${qs}` : '/api/v1/admin/corpus/documents';
  return getJson<ListCorpusDocumentsDto>(path);
}

export async function getCorpusStats(): Promise<CorpusStatsDto> {
  return getJson<CorpusStatsDto>('/api/v1/admin/corpus/documents/stats');
}

export async function listCorpusTags(): Promise<{ readonly tags: readonly string[] }> {
  return getJson<{ tags: readonly string[] }>('/api/v1/admin/corpus/documents/tags');
}

export async function deleteCorpusDocument(docId: string): Promise<void> {
  const response = await fetch(
    buildUrl(`/api/v1/admin/corpus/documents/${encodeURIComponent(docId)}`),
    {
      method: 'DELETE',
      headers: { ...authHeader() },
      credentials: 'include',
    },
  );
  if (!response.ok) {
    throw await parseProblem(response);
  }
}
